import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Timer, Loader2, CheckCircle2, AlertTriangle, Volume2 } from 'lucide-react';

/**
 * LatencyCalibrationDialog — walks the artist through a one-tap latency test.
 *
 * Playback: the engine plays a short 2 kHz click through the speaker while
 * the mic records; we detect the click in the recording and store the
 * roundtrip. The measured value is written to localStorage and used by
 * stopRecording to align the recorded vocal with the beat.
 */
export function LatencyCalibrationDialog({ open, onOpenChange, engine }) {
  const { latencyMs, calibration, calibrateLatency, resetCalibration, dismissCalibration } = engine;
  const [busy, setBusy] = useState(false);
  const status = calibration.status;
  const measuredMs = calibration.measuredMs;
  const error = calibration.error;

  const handleRun = async () => {
    setBusy(true);
    try { await calibrateLatency(); } catch (e) { /* handled in engine */ }
    setBusy(false);
  };

  const handleClose = (o) => {
    if (busy) return;
    onOpenChange(o);
    if (!o) dismissCalibration();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-[#0a0a0a] border border-[#262626]" data-testid="latency-cal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lime">
            <Timer className="w-5 h-5" />
            Latency Calibration
          </DialogTitle>
          <DialogDescription className="text-[#999] text-xs pt-1">
            Measures the exact speaker → mic roundtrip on your device so your recorded vocals land in time with the beat. Takes about 2 seconds.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="bg-[#141414] border border-[#262626] rounded-lg p-3 text-xs text-[#bbb] flex items-start gap-2" data-testid="latency-cal-instructions">
            <Volume2 className="w-4 h-4 text-lime mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-white mb-1">Before you start</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Turn your device volume up (medium is fine).</li>
                <li><span className="text-amber-300">Take your headphones off</span> so the mic can hear the click.</li>
                <li>Sit in a quiet spot.</li>
              </ul>
            </div>
          </div>

          {latencyMs != null && status === 'idle' && (
            <div className="bg-lime/5 border border-lime/30 rounded-lg p-3 text-xs" data-testid="latency-cal-current">
              Current calibration: <span className="text-lime font-mono">{latencyMs} ms</span>
            </div>
          )}

          {status === 'preparing' && (
            <div className="flex items-center gap-2 text-xs text-[#bbb]" data-testid="latency-cal-preparing">
              <Loader2 className="w-4 h-4 animate-spin text-lime" />
              Requesting microphone…
            </div>
          )}
          {status === 'recording' && (
            <div className="flex items-center gap-2 text-xs text-amber-300" data-testid="latency-cal-recording">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              Listening for the click… (don't move the device)
            </div>
          )}
          {status === 'done' && measuredMs != null && (
            <div className="bg-lime/10 border border-lime/40 rounded-lg p-3 text-xs flex items-center gap-2" data-testid="latency-cal-success">
              <CheckCircle2 className="w-4 h-4 text-lime shrink-0" />
              <span>
                Measured roundtrip: <span className="text-lime font-mono">{measuredMs} ms</span>. Saved — your next take will be aligned automatically.
              </span>
            </div>
          )}
          {status === 'error' && (
            <div className="bg-red-950/40 border border-red-500/40 rounded-lg p-3 text-xs flex items-start gap-2" data-testid="latency-cal-error">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <span className="text-red-300">{error || 'Calibration failed. Please retry.'}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 mt-2">
          <Button
            size="sm" variant="outline"
            className="border-[#333] text-xs text-[#aaa]"
            onClick={() => { resetCalibration(); }}
            disabled={busy || latencyMs == null}
            data-testid="latency-cal-reset-btn"
          >
            Reset
          </Button>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline"
              className="border-[#333]"
              onClick={() => handleClose(false)}
              disabled={busy}
              data-testid="latency-cal-close-btn"
            >
              Close
            </Button>
            <Button
              size="sm" className="btn-primary"
              onClick={handleRun}
              disabled={busy}
              data-testid="latency-cal-run-btn"
            >
              {busy ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />Measuring…</>) : (latencyMs != null ? 'Re-calibrate' : 'Start Calibration')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
